import { useState, useEffect } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useRootStore } from '../hooks/useRootStore';
import { match } from 'ts-pattern';
import { observer } from 'mobx-react-lite';
import { Box, Button, Card, CardHeader, Divider, IconButton, Link, Stack, TextField, Tooltip } from '@mui/material';
import './Issue.css';
import moment from 'moment';
import momentDurationFormatSetup from "moment-duration-format";
import '@mdxeditor/editor/style.css'
import IssueEdit from './IssueEdit';
import { IssueState } from '../data/IssueState';
import type { Issue } from '../data/Issue.ts';
import IssueActivityList from './IssueActivityList.tsx';
import IssueAttachesWidget from './IssueWidgets/IssueAttachesWidget.tsx';
import IssueMainWidget from './IssueWidgets/IssueMainWidget.tsx';
import MarkdownView from "./MarkdownView";
import IssueTrackerWidget from './IssueWidgets/IssueTrackerWidget.tsx';
import IssueErrorCausersWidget from './IssueWidgets/IssueErrorCausersWidget.tsx';
import IssuePreferredWorkersWidget from './IssueWidgets/IssuePreferredWorkersWidget.tsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
momentDurationFormatSetup(moment);

const Issue = observer(() => {
    const portrait = useMediaQuery("(orientation: portrait)");
    const mobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const { api, routerStore, userProfile } = useRootStore();
    const id = routerStore.routerState.params.id as unknown as number;

    const [issueLoaded, setIssueLoaded] = useState(false);
    const [issue, setIssue] = useState<Issue | null>(null);
    
    const [issueEditing, setIssueEditing] = useState(false);
    const [clientTrackerEditing, setClientTrackerEditing] = useState(false);
    const [asideTop, setAsideTop] = useState(0);
    const [issueTextExpanded, setIssueTextExpanded] = useState(false);

    useEffect(() => {
        api.getIssue(id).then(issue => {
            setIssue(issue);
            if (issue?.StateID == IssueState.stub) {
                setIssueEditing(true);
            }
            setIssueLoaded(true);
        });

        setAsideTop((document.getElementsByTagName('header')[0]).offsetHeight);
        
    }, [id, api]);

    const updateExternalTrackerLink = () => {
        const link = (document.getElementById('external-tracker-link') as HTMLInputElement)?.value ?? '';
        api.updateIssueExternalTrackerLink(id, link)
            .then(() => {
                setIssue({ ...issue!, ExternalTrackerLink: link });
                setClientTrackerEditing(false);
            });
    }

    return (
        match([issue, issueLoaded])
            .with([null, false], () => '')
            .with([null, true], () => <div>Issue not found</div>)
            .otherwise(() =>
                <Box id="issue">
                    <Box flex={1} sx={{p:2}}>               
                        <Box sx={{ textAlign: 'left' }}>
                            {!issueEditing ?
                                <>
                                    <Box className="issue-subject">
                                        <Box sx={{ fontWeight: 'bold', textAlign: 'left', pl: 0, }}>
                                            <span style={{color: '#444444'}}>#{issue?.ID} .</span> {issue?.Subject}
                                        </Box>                                            
                                        <Button
                                            id="edit-changed"
                                            disabled={![IssueState.stub, IssueState.backlog, IssueState.queued].includes((issue?.StateID ?? -1) as IssueState) ||
                                                issue!.AuthorID != userProfile!.ID}
                                            onClick={() => setIssueEditing(true)}
                                        >
                                            Редактировать
                                        </Button>
                                    </Box>
                                    <Divider />
                                    {issue?.IssueText?.trim() == '' ? '' :
                                        <Box className='issue-text-view'>
                                            <Box className={"issue-text-view-text" + (issueTextExpanded ? ' expanded' : '')}>
                                                <MarkdownView markup={issue?.IssueText ?? ''} />
                                            </Box>
                                            <Tooltip title={issueTextExpanded ? 'свернуть' : 'смотреть полностью'}>
                                                <ExpandMoreIcon sx={{ rotate: issueTextExpanded ? '180deg' : '0 deg', transition: 'rotate 0.3s' }} onClick={() => setIssueTextExpanded(!issueTextExpanded)} />
                                            </Tooltip>
                                        </Box>
                                    }
                                    <Divider />
                                    {!(mobile && portrait) ? '' :
                                        <Stack
                                            direction="column"
                                            spacing={3}
                                            padding={0}
                                            className="issue-widgets"
                                        >
                                            <IssueMainWidget issue={issue!} onIssueChanged={(changed) => setIssue(changed)} />
                                            <IssueTrackerWidget issueId={id} estimationHours={issue!.EstimationHours} />
                                            {issue!.IsError ? <IssueErrorCausersWidget issue={issue!} /> : ''}
                                            {!issue!.Project?.NoQueueScheduler && [IssueState.backlog, IssueState.queued, IssueState.stub].includes(issue!.StateID)
                                                ? <IssuePreferredWorkersWidget issue={issue!} />
                                                : ''
                                            }
                                        </Stack>
                                    }
                                    <IssueActivityList issueId={issue!.ID} />
                                </>
                                :
                                <IssueEdit issue={issue!} onSave={(changed: Issue) => { if (issue?.StateID == IssueState.stub) window.location.href = `issues/${changed.ID}`; else { setIssue(changed); setIssueEditing(false); } }} onCancel={() => setIssueEditing(false)} />
                            }
                        </Box>
                    </Box>
                    <Divider orientation={portrait ? 'horizontal' : 'vertical'} flexItem />
                    {issue?.StateID == IssueState.stub || (mobile && portrait)
                        ? '' :
                        <Box style={{ top: portrait ? 'initial' : asideTop, height: portrait ? 'initial' : 'calc(100vh - 29px - ' + asideTop + 'px)' }} className="issue-aside" id="issue-aside">
                            <Stack
                                direction="column"
                                spacing={2}
                                padding={2}
                            >
                                <IssueMainWidget issue={issue!} onIssueChanged={(changed) => setIssue(changed)} />
                                <IssueTrackerWidget issueId={id} estimationHours={issue!.EstimationHours} />
                                {issue!.IsError ? <IssueErrorCausersWidget issue={issue!} /> : ''}
                                {!issue!.Project?.NoQueueScheduler && [IssueState.backlog, IssueState.queued, IssueState.stub].includes(issue!.StateID)
                                    ? <IssuePreferredWorkersWidget issue={issue!} />
                                    : ''
                                }
                                <Card className="issue-widget" sx={{ position: 'relative' }}>
                                    <CardHeader title='Клиентский трекер'/>
                                    <Box sx={{ mb: 2 }}>
                                        <Box className="issue-widget-editable-value" sx={{ display: 'block' }}>
                                            <Box>
                                                {issue?.ExternalTrackerLink && !clientTrackerEditing
                                                    ?
                                                    <Box sx={{ display: 'flex', alignItems:'center' }} >
                                                        <Link href={issue?.ExternalTrackerLink} title="Перейти">{issue?.ExternalTrackerLink}</Link>
                                                        <IconButton className="edit-btn" style={{margin:'0'}} onClick={() => setClientTrackerEditing(!clientTrackerEditing)} title='Изменить'>
                                                            <ModeEditIcon sx={{ p: 0, m: 0, }} />
                                                        </IconButton>
                                                    </Box>
                                                    :
                                                    <Box sx={{ textAlign: 'center', pt: 2 }}>
                                                        <TextField id="external-tracker-link" sx={{ mt: 1, width: "100%" }} defaultValue={issue?.ExternalTrackerLink} />
                                                        <Button sx={{ mt: 2 }} onClick={() => updateExternalTrackerLink()}>Сохранить</Button>
                                                    </Box>
                                                }
                                            </Box>
                                        </Box>
                                    </Box>
                                </Card>
                                <IssueAttachesWidget issueId={id} />
                            </Stack>
                        </Box>
                    }
                </Box>
            )
    )
})

export default Issue;